import React, { useState, useEffect } from "react";

import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  HiOutlineChevronDoubleLeft as ArrowLeft,
  HiOutlineChevronDoubleRight as ArrowRight,
} from "react-icons/hi";

import { Screenshot } from "../../../../models/screenshot";
import ScreenshotService from "../../../../services/screenshot.service";
import ImageCarousel from "../../../Generic/ImageCarousel";
import { NotificationToast } from "../../../Generic/Toast/NotificationToast";
import useVideoContext from "../../hooks/useVideoContext/useVideoContext";
import MainParticipant from "../../MainParticipant/MainParticipant";
import useSurveyStyles from "../styles";
import useStyles from "./styles";

const ConsultantSection = () => {
  const surveyClasses = useSurveyStyles();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const { screenshots, setScreenshots, task } = useVideoContext();

  const [selectedImage, setSelectedImage] = useState<string>("");
  const [isOpenImageModal, setIsOpenImageModal] = useState<boolean>(false);
  const [screenshotListCollapsed, setScreenshotListCollapsed] = useState<boolean>(false);

  const { isFetching: loadingScreenshots, isFetched } = useQuery(
    ["video.survey.screenshot.list", task?.id],
    () =>
      ScreenshotService.getAll({
        taskId: task?.id,
      }),
    {
      enabled: !!task?.id,
      onSuccess: (response) => {
        if (response?.data) setScreenshots(response.data);
      },
      onError: () => {
        NotificationToast.warning(translate("couldNotRetrieveScreenshots"));
      },
    }
  );

  useEffect(() => {
    if (isFetched && !screenshots.length) {
      setScreenshotListCollapsed(true);
    }
  }, [screenshots]);

  const renderArrowButton = () => {
    const handleCollapse = () => setScreenshotListCollapsed((state) => !state);

    if (screenshotListCollapsed) {
      return (
        <div data-cy="openCollapse" onClick={handleCollapse} className={classes.collapseButton}>
          <ArrowRight />
        </div>
      );
    }

    return (
      <div data-cy="closeCollapse" onClick={handleCollapse} className={classes.collapseButton}>
        <ArrowLeft />
        <span>{translate("collapse")}</span>
      </div>
    );
  };

  const renderImgStack = () => {
    const handleSelectImage = (link?: string) => {
      if (link) {
        setSelectedImage(link);
        setIsOpenImageModal(true);
      }
    };

    if (loadingScreenshots) {
      return (
        <Stack className={classes.screenshotList}>
          {Array.from({ length: 10 }).map((_, index: number) => (
            <Skeleton key={index} variant="text" height="100px" width="98px" />
          ))}
        </Stack>
      );
    }

    return (
      <Stack data-cy="imgSection" className={classes.screenshotList} spacing={1}>
        {screenshots.map((screenshot: Screenshot, index: number) => (
          <img
            key={index}
            className={classes.screenshot}
            src={screenshot.link}
            onClick={() => handleSelectImage(screenshot.link)}
          />
        ))}
      </Stack>
    );
  };

  const renderScreenshotList = () => {
    return (
      <div>
        {renderArrowButton()}
        {!screenshotListCollapsed ? renderImgStack() : null}
      </div>
    );
  };

  return (
    <div className={surveyClasses.sectionConsultant}>
      <ImageCarousel
        selectedUrl={selectedImage}
        urls={screenshots.map((screenshot) => screenshot.link)}
        open={isOpenImageModal}
        handleClose={() => setIsOpenImageModal(false)}
      />

      {renderScreenshotList()}
      <MainParticipant />
    </div>
  );
};

export default ConsultantSection;
